import { camelizeKeys } from 'humps';
import { Paths } from '../components/shared/url_helpers';
import CoreClient from './core_client';
import * as elog from '@reverbdotcom/commons/src/elog';
import { core_apimessages_CheckoutPaymentResponse as CheckoutPaymentResponse } from '../schemas/rql';

export class ApplePayCoreClient {
  listingCurrency: string;

  totalPrice: V3Money;

  constructor(listingCurrency: string) {
    this.listingCurrency = listingCurrency;
  }

  /**
   * Fetch Line Items based on selected shipping contact
   *
   * See {@link https://github.com/reverbdotcom/reverb/blob/master/app/reverb_api/v3/controllers/checkout/mobile_pay/line_items.rb backend definition}
   * for input params
   */
  async fetchLineItems(shippingContact: ApplePayJS.ApplePayPaymentContact):
  Promise<{ total: ApplePayJS.ApplePayLineItem; lineItems: ApplePayJS.ApplePayLineItem[]}> {

    const url = Paths.apiCheckoutApplePayLineItems.expand({
      listingCurrency: this.listingCurrency,
      ...paymentContactToAddressParams(shippingContact),
    });

    try {
      const { line_items }: { line_items: LineItem[] } = await CoreClient.get(url);
      const totalLineItem = findTotalLineItem(line_items);

      this.totalPrice = getTotal(totalLineItem);

      return {
        total: applePayLineItem(totalLineItem),
        lineItems: applePayLineItems(line_items),
      };
    } catch (e) {
      elog.error('checkout.apple_pay.fetch_line_items_error', {}, e);
      throw e;
    }
  }

  async makePayment(payment: ApplePayJS.ApplePayPayment): Promise<CheckoutPaymentResponse> {
    const url = Paths.apiCheckoutApplePayPayment.expand({ listingCurrency: this.listingCurrency });

    try {
      const result = await CoreClient.post(url, {
        total_price: this.totalPrice,
        shipping_address: paymentContactToAddressParams(payment.shippingContact),
        adyen_token: btoa(JSON.stringify(payment.token.paymentData)),
      });
      return camelizeKeys(result);
    } catch (e) {
      elog.error('checkout.apple_pay.make_payment_error', {}, e);
      throw e;
    }
  }
}

function findTotalLineItem(lineItems) {
  return lineItems.find((lineItem) => lineItem.name === 'amount_owed');
}

function applePayLineItem(lineItem): ApplePayJS.ApplePayLineItem {
  return {
    amount: getTotal(lineItem).amount,
    label: lineItem.description,
    type: 'final',
  };
}

function getTotal(lineItem: LineItem) {
  return lineItem.amount.original;
}

function applePayLineItems(lineItems) {
  return lineItems.map(applePayLineItem);
}

function paymentContactToAddressParams(shippingContact: ApplePayJS.ApplePayPaymentContact): AddressParams {
  return {
    name: [shippingContact.givenName, shippingContact.familyName].join(' '),
    street_address: shippingContact.addressLines && shippingContact.addressLines[0],
    extended_address: shippingContact.addressLines && shippingContact.addressLines[1],
    locality: shippingContact.locality,
    postal_code: shippingContact.postalCode,
    region: shippingContact.administrativeArea,
    country_code: shippingContact.countryCode?.toUpperCase(),
    phone: shippingContact.phoneNumber,
  };
}

interface LineItem {
  name: string;
  description: string;
  amount: { display: V3Money; original: V3Money; }
}

export interface V3Money {
  amount: string;
  amount_cents: number;
  currency: string;
  symbol: string;
  display: string;
}

/**
 * See {@link https://github.com/reverbdotcom/reverb/blob/master/app/reverb_api/v1/helpers/addresses/address_params.rb address_params}
 * definition for details
 */
interface AddressParams {
  name: string;
  street_address: string;
  extended_address?: string;
  locality: string;
  region: string;
  postal_code: string;
  country_code: string;
  phone: string;
  primary?: boolean;
}
